import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActingOrg } from '../context/ActingOrgContext';
import { useAuth } from '../context/AuthContext';
import { orgManagementService, Organization } from '../services/orgManagementService';

/**
 * 机构管理模式横幅。
 * 仅当 super admin 代入某机构（actingOrg 非空）时显示。
 * 普通机构 admin 即便 localStorage 残留 acting_org 也绝不显示（仅 super 可代入）。
 * 提供正式的管理态展示与两类操作：
 *   - 切换机构：下拉选择其他机构，直接跳转其管理态（无需返回机构列表页）。
 *   - 退出机构管理：清除代入态，回到全局视图。
 */
const ActingOrgBanner: React.FC = () => {
  const { actingOrg, enterOrg, exitOrg } = useActingOrg();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const switcherRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭下拉
  useEffect(() => {
    if (!switcherOpen) return;
    const onClick = (e: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [switcherOpen]);

  const openSwitcher = useCallback(async () => {
    const next = !switcherOpen;
    setSwitcherOpen(next);
    if (next && orgs.length === 0) {
      setLoadingOrgs(true);
      setLoadError(null);
      try {
        setOrgs(await orgManagementService.listOrgs());
      } catch (e: any) {
        setLoadError(e?.response?.data?.message || e?.message || '加载机构列表失败');
      } finally {
        setLoadingOrgs(false);
      }
    }
  }, [switcherOpen, orgs.length]);

  const handleSwitch = useCallback(
    (org: Organization) => {
      setSwitcherOpen(false);
      if (org.id === actingOrg?.id) return;
      enterOrg({ id: org.id, name: org.name });
      navigate('/dashboard');
    },
    [actingOrg?.id, enterOrg, navigate]
  );

  const handleExit = useCallback(() => {
    setSwitcherOpen(false);
    exitOrg();
    navigate('/org-management');
  }, [exitOrg, navigate]);

  // 仅 super admin 可见;普通 admin 即便 localStorage 残留 acting_org 也不显示。
  if (!isSuperAdmin || !actingOrg) return null;

  return (
    <div className="bg-gradient-to-r from-amber-50 to-amber-100/60 border-b border-amber-200 px-8 py-3 flex items-center justify-between flex-shrink-0">
      {/* 左侧：管理态标识 + 机构名 */}
      <div className="flex items-center space-x-3 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
          <span className="material-symbols-outlined text-amber-700" style={{ fontSize: '20px' }}>
            corporate_fare
          </span>
        </div>
        <div className="min-w-0">
          <div className="flex items-center space-x-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 font-label">
              机构管理模式
            </span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-amber-500/20 text-[10px] font-semibold text-amber-800">
              SUPER ADMIN
            </span>
          </div>
          <p className="text-sm font-semibold text-amber-900 truncate font-body" title={actingOrg.name}>
            正在管理 · {actingOrg.name}
          </p>
        </div>
      </div>

      {/* 右侧：切换 / 退出 */}
      <div className="flex items-center space-x-2 flex-shrink-0">
        {/* 切换机构 */}
        <div className="relative" ref={switcherRef}>
          <button
            type="button"
            onClick={openSwitcher}
            aria-haspopup="listbox"
            aria-expanded={switcherOpen}
            className="flex items-center space-x-1.5 px-3 py-2 text-sm font-medium text-amber-800 bg-white/70 hover:bg-white rounded-lg border border-amber-200 transition-colors font-body"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
              swap_horiz
            </span>
            <span>切换机构</span>
            <span
              className="material-symbols-outlined transition-transform"
              style={{ fontSize: '18px', transform: switcherOpen ? 'rotate(180deg)' : 'none' }}
            >
              expand_more
            </span>
          </button>

          {switcherOpen && (
            <div
              role="listbox"
              className="absolute right-0 mt-2 w-72 max-h-80 overflow-auto bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50"
            >
              <div className="px-3 pb-2 mb-1 border-b border-gray-100 text-[11px] font-semibold uppercase tracking-wider text-gray-400 font-label">
                选择要管理的机构
              </div>
              {loadingOrgs && (
                <div className="px-3 py-4 text-sm text-gray-400 text-center font-body">加载中…</div>
              )}
              {loadError && (
                <div className="px-3 py-4 text-sm text-error text-center font-body">{loadError}</div>
              )}
              {!loadingOrgs && !loadError && orgs.length === 0 && (
                <div className="px-3 py-4 text-sm text-gray-400 text-center font-body">暂无机构</div>
              )}
              {!loadingOrgs &&
                !loadError &&
                orgs.map((org) => {
                  const current = org.id === actingOrg.id;
                  return (
                    <button
                      key={org.id}
                      type="button"
                      role="option"
                      aria-selected={current}
                      onClick={() => handleSwitch(org)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-sm transition-colors font-body ${
                        current ? 'bg-amber-50 text-amber-900 cursor-default' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{org.name}</p>
                        <p className="text-xs text-gray-400 truncate">
                          {org.schoolCount} 所学校 · {org.adminCount} 名管理员
                        </p>
                      </div>
                      {current && (
                        <span className="material-symbols-outlined text-amber-600 flex-shrink-0 ml-2" style={{ fontSize: '18px' }}>
                          check
                        </span>
                      )}
                    </button>
                  );
                })}
            </div>
          )}
        </div>

        {/* 退出机构管理 */}
        <button
          type="button"
          onClick={handleExit}
          className="flex items-center space-x-1.5 px-3 py-2 text-sm font-medium text-amber-900 bg-amber-500/20 hover:bg-amber-500/30 rounded-lg transition-colors font-body"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
            logout
          </span>
          <span>退出机构管理</span>
        </button>
      </div>
    </div>
  );
};

export default ActingOrgBanner;
